import { ZfFormField, ZfInput, ZfStack, ZfTextButton, ZfTypography } from '@zafire/react';
import type { FunctionComponent } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCreateTool, useEnabledDomiciles, useUpdateTool } from '../../../api';
import type { ITool } from '../../../interfaces/tool';
import { MultiLanguageEditor } from '../../atoms';
import { DomicileSelection } from '../domicile-selection';

interface IProps {
    tool: ITool;
}

export const ToolEditor: FunctionComponent<IProps> = ({ tool: initialTool }) => {
    const { t } = useTranslation();
    const { data: availableDomiciles } = useEnabledDomiciles();

    const { mutate: createTool } = useCreateTool();
    const { mutate: updateTool } = useUpdateTool();

    const [tool, setTool] = useState(initialTool);
    const [hasChanges, setHasChanges] = useState(false);
    const isNew = tool.id === -1;

    useEffect(() => setTool(initialTool), [initialTool]);

    const onToolChange = (updatedLink: Partial<ITool>) => {
        setHasChanges(true);
        setTool({ ...tool, ...updatedLink });
    };

    const handleSaveChanges = () => {
        if (isNew) createTool(tool);
        else updateTool(tool);
        setHasChanges(false);
    };

    return (
        <ZfStack direction="column" spacing="large">
            <ZfStack direction="row" alignment="center" justify="space-between">
                <ZfTypography variant="heading-small">{isNew ? t('contentAdmin.createNewTool') : t('contentAdmin.editTool')}</ZfTypography>
                <ZfTextButton hierarchy="secondary" disabled={!tool.name || !tool.link || !hasChanges} onClick={handleSaveChanges}>
                    {t('contentAdmin.saveChanges')}
                </ZfTextButton>
            </ZfStack>
            <ZfTypography variant="body-medium">{t('contentAdmin.toolUrlHelp')}</ZfTypography>
            <ZfFormField label={t('contentAdmin.toolLinkLabel')} mandatory={true}>
                <ZfInput type="text" value={tool.link} onInputChange={(e) => onToolChange({ link: e.detail })} />
            </ZfFormField>
            <MultiLanguageEditor
                label={t('contentAdmin.toolTitleLabel')}
                value={tool.name}
                onChange={(name) => onToolChange({ name })}
                multiline={false}
                markdown={false}
                collapsible={true}
                expanded={true}
            />
            <DomicileSelection
                global={tool.global}
                onChange={(includedDomiciles, excludedDomiciles, isGlobal) => onToolChange({ global: isGlobal, excludedDomiciles, includedDomiciles })}
                available={availableDomiciles ?? []}
                excludedIn={tool.excludedDomiciles}
                includedIn={tool.includedDomiciles}
            />
        </ZfStack>
    );
};
